import { createSlice, type PayloadAction } from '@reduxjs/toolkit'
import type { EnhancedMarketId } from '#api/enhancements/enhancedTmsApiTypes'
import type {
  AdminNatCodesCodeDescription,
  AdminNatCodesFamilyDescription,
  AdminNatCodesGroup,
  AdminNatCodesGroupDescription,
} from '#api/tmsApiService.ts'
import type { SerializedError } from '#features/ErrorHandling/errorHelpers'

export interface NationalCodesMarketState {
  nationalCodes?: AdminNatCodesGroup[]
  // the ids of the entries that are currently being worked on
  // groupId | familyId | codeId (not executionId)
  pendingTasks: string[]
  status: 'initial' | 'loading' | 'loaded' | 'failed'
  error?: SerializedError
}

export interface NationalCodesState {
  byMarket: Record<EnhancedMarketId, NationalCodesMarketState | undefined>
}

const initialState: NationalCodesState = {
  byMarket: {},
}

export const nationalCodesSlice = createSlice({
  name: 'nationalCodes',
  initialState,
  reducers: {
    _setNationalCodes: (
      state,
      action: PayloadAction<{
        marketId: EnhancedMarketId
        nationalCodes: AdminNatCodesGroup[]
      }>,
    ) => {
      const { marketId, nationalCodes } = action.payload
      const entry = state.byMarket[marketId]
      if (!entry) {
        state.byMarket[marketId] = { status: 'loaded', pendingTasks: [], nationalCodes }
        return
      }
      entry.nationalCodes = nationalCodes
    },
    _setMarketStatus: (
      state,
      action: PayloadAction<
        {
          marketId: EnhancedMarketId
        } & Pick<NationalCodesMarketState, 'status' | 'error'>
      >,
    ) => {
      const { marketId, status, error } = action.payload
      const entry = state.byMarket[marketId]
      if (!entry) {
        state.byMarket[marketId] = { status, error, pendingTasks: [] }
        return
      }
      entry.status = status
      entry.error = error
    },
    _pushOrReplaceGroup: (
      state,
      action: PayloadAction<{
        marketId: EnhancedMarketId
        groupId: string
        group: AdminNatCodesGroupDescription | null
      }>,
    ) => {
      const { marketId, groupId, group } = action.payload
      const entry = state.byMarket[marketId]
      if (!entry?.nationalCodes) {
        return
      }
      const index = entry.nationalCodes.findIndex((g) => g.groupId === groupId)

      if (group === null) {
        if (index !== -1) {
          entry.nationalCodes.splice(index, 1)
        }
        return
      }

      if (index !== -1) {
        entry.nationalCodes[index] = {
          ...entry.nationalCodes[index],
          ...group,
        }
      } else {
        entry.nationalCodes.push({
          families: [],
          groupId,
          ...group,
        })
      }
    },
    _pushOrReplaceFamily: (
      state,
      action: PayloadAction<{
        marketId: EnhancedMarketId
        groupId: string
        familyId: string
        family: AdminNatCodesFamilyDescription | null
      }>,
    ) => {
      // TODO does not handle groupId changes
      const { marketId, groupId, familyId, family } = action.payload
      const entry = state.byMarket[marketId]
      if (!entry?.nationalCodes) {
        return
      }

      const groupIndex = entry.nationalCodes.findIndex((g) => g.groupId === groupId)
      if (groupIndex === -1) {
        return
      }
      const group = entry.nationalCodes[groupIndex]
      if (!group.families) {
        group.families = []
      }

      const familyIndex = group.families.findIndex((f) => f.familyId === familyId)

      if (family === null) {
        if (familyIndex !== -1) {
          group.families.splice(familyIndex, 1)
        }
        return
      }

      if (familyIndex !== -1) {
        group.families[familyIndex] = {
          ...group.families[familyIndex],
          ...family,
        }
      } else {
        group.families.push({
          codes: [],
          familyId,
          ...family,
        })
      }
    },
    _pushOrReplaceVariant: (
      state,
      action: PayloadAction<{
        marketId: EnhancedMarketId
        groupId: string
        familyId: string
        executionId: string
        code: AdminNatCodesCodeDescription | null
      }>,
    ) => {
      // TODO does not handle groupId or familyId changes
      const { marketId, groupId, familyId, executionId, code } = action.payload
      const entry = state.byMarket[marketId]
      if (!entry?.nationalCodes) {
        return
      }

      const groupIndex = entry.nationalCodes.findIndex((g) => g.groupId === groupId)
      if (groupIndex === -1) {
        return
      }
      const group = entry.nationalCodes[groupIndex]
      if (!group.families) {
        group.families = []
      }

      const familyIndex = group.families.findIndex((f) => f.familyId === familyId)
      if (familyIndex === -1) {
        return
      }

      const family = group.families[familyIndex]
      if (!family.codes) {
        family.codes = []
      }

      const codeIndex = family.codes.findIndex((c) => c.executionId === executionId)

      if (code === null) {
        if (codeIndex !== -1) {
          family.codes.splice(codeIndex, 1)
        }
        return
      }

      if (codeIndex !== -1) {
        family.codes[codeIndex] = {
          ...family.codes[codeIndex],
          ...code,
        }
      } else {
        family.codes.push({
          executionId: executionId,
          codeId: `${family.familyId}${executionId}`,
          ...code,
        })
      }
    },
    loadNationalCodesForMarket: (_state, _action: PayloadAction<{ marketId: EnhancedMarketId; forceReload?: boolean } | undefined>) => {
      // executed in the middleware
    },
    _addPendingTask: (
      state,
      action: PayloadAction<{
        marketId: EnhancedMarketId
        taskId: string
      }>,
    ) => {
      const marketState = state.byMarket[action.payload.marketId]
      if (!marketState) {
        return
      }
      marketState.pendingTasks.push(action.payload.taskId)
    },
    _removePendingTask: (
      state,
      action: PayloadAction<{
        marketId: EnhancedMarketId
        taskId: string
      }>,
    ) => {
      const marketState = state.byMarket[action.payload.marketId]
      if (!marketState) {
        return
      }
      const index = marketState.pendingTasks.indexOf(action.payload.taskId)
      if (index !== -1) {
        marketState.pendingTasks.splice(index, 1)
      }
    },
  },
})

export const {
  loadNationalCodesForMarket,
  _pushOrReplaceGroup,
  _pushOrReplaceFamily,
  _pushOrReplaceVariant,
  _setMarketStatus,
  _setNationalCodes,
  _addPendingTask,
  _removePendingTask,
} = nationalCodesSlice.actions
export const nationalCodesActions = nationalCodesSlice.actions
